import { Component, Input, ViewChild, OnInit, AfterViewInit, ViewChildren, QueryList } from '@angular/core';
import * as THREE from 'three';
import { NgxCameraComponent, NgxMeshComponent, NgxRendererComponent, NgxSceneComponent } from 'ngx3js';

interface WaveParams {
  height: number;
  frequency: number;
  speed: number;
}

interface LiquidLayer {
  color: string;
  percentage: number;
  mesh?: THREE.Mesh;
  basePositions?: Float32Array;
  waveParams?: WaveParams[];
}

@Component({
  selector: 'app-view-warehouse-container-multi',
  templateUrl: './view-warehouse-container-multi.component.html',
  styleUrls: ['./view-warehouse-container-multi.component.scss']
})
export class ViewWarehouseContainerMultiComponent {
  @ViewChild('renderer3d', { static: false }) rendererComp!: NgxRendererComponent;
  @ViewChild('camera', { static: false }) camera!: NgxCameraComponent;
  @ViewChild('scene', { static: false }) scene!: NgxSceneComponent;
  @ViewChild('containerMesh', { static: false }) containerMesh!: NgxMeshComponent;
  @ViewChildren('liquidMeshes') liquidMeshes!: QueryList<NgxMeshComponent>;

  @Input() width: number = 0;
  @Input() height: number = 0;
  @Input() depth: number = 0;
  @Input() percentageFull: number = 0;
  @Input() liquidColor: string = "#0086E3";
  @Input() liquids: LiquidLayer[] = [
    { color: '#00E300', percentage: 0.1, waveParams: [{ height: 0.25, frequency: 4, speed: 4 }] },
    { color: '#FFFF00', percentage: 0.3, waveParams: [{ height: 0.25, frequency: 4, speed: 4 }] },
    { color: '#ff5555', percentage: 0.2, waveParams: [{ height: 0.25, frequency: 4, speed: 4 }] },
  ];

  angle = 0;
  mathPiValue: number = Math.PI;
  minusMathPiValue: number = this.mathPiValue * (-1);

  tooltipVisible = false;
  tooltipText = 'Contenido del cilindro, si funcionar <br> asdfasd asdfasd Aleste es la linea';
  tooltipX = 0;
  tooltipY = 0;

  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private originalColor: THREE.Color | null = null;

  material = new THREE.MeshStandardMaterial({
    color: 0x888888,       // tono base del metal
    metalness: 1.0,         // 1.0 = completamente metálico
    roughness: 0.2,         // entre 0 y 1 (0 = muy reflectivo)
    envMapIntensity: 1.0,   // refuerza el reflejo del entorno
  });

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.adjustCameraToBox();
      // this.updateTooltipCenter(); // ← aquí
      this.liquids.forEach((l, i) => {
        const mesh = this.getMesh(i);
        if (mesh) {
          l.mesh = mesh;
          const geometry = mesh.geometry as THREE.BufferGeometry;
          l.basePositions = geometry.attributes.position.array.slice() as Float32Array;
        }
      });

      this.startAnimation();
    }, 100);

    const mesh = this.containerMesh?.getMesh();
    if (mesh) {
      const mat = mesh.material as THREE.MeshStandardMaterial;
      this.originalColor = mat.color.clone(); // Guardamos color original
    }
  }

  private adjustCameraToBox() {
    if (!this.camera) return;

    const maxDimension = Math.max(this.width, this.depth, this.height);
    const camDistance = maxDimension * 1.5;

    const angle = Math.PI / 4;
    const x = -camDistance * Math.cos(angle);
    const y = this.height / 2;
    const z = -camDistance * Math.sin(angle);

    this.camera.setPosition(x, y, z);

    const mesh = this.containerMesh?.getMesh();
    if (mesh) {
      const bbox = new THREE.Box3().setFromObject(mesh);
      const center = new THREE.Vector3();
      bbox.getCenter(center);
      this.camera.setLookat(center.x, center.y, center.z);
    }
  }

  onMouseMove(event: MouseEvent) {
    const canvas = this.rendererComp.getRenderer().domElement;
    const rect = canvas.getBoundingClientRect();

    // Coordenadas normalizadas [-1,1] para raycaster
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.checkIntersection(event.clientX - rect.left, event.clientY - rect.top);
  }

  private checkIntersection(mouseX: number, mouseY: number) {
    if (!this.camera || !this.containerMesh) return;

    const cam = this.camera.getCamera();
    this.raycaster.setFromCamera(this.mouse, cam);

    const mesh = this.containerMesh.getMesh();
    if (!mesh) return;

    const intersects = this.raycaster.intersectObject(mesh);

    const mat = mesh.material as THREE.MeshStandardMaterial;
    if (intersects.length > 0) {
      this.tooltipVisible = true;

      // Posición exacta del mouse dentro del canvas
      this.tooltipX = mouseX;
      this.tooltipY = mouseY - 20; // 20px por encima del cursor

      mat.color.set('#FFFF99'); // amarillo semi
    } else {
      this.tooltipVisible = false;
      if (this.originalColor) mat.color.copy(this.originalColor);
    }
  }

  private updateTooltipCenter() {
    const mesh = this.containerMesh?.getMesh();
    const cam = this.camera?.getCamera();
    const renderer = this.rendererComp?.getRenderer();
    if (!mesh || !cam || !renderer) return;

    // Calculamos el centro del mesh
    const bbox = new THREE.Box3().setFromObject(mesh);
    const center = new THREE.Vector3();
    bbox.getCenter(center);

    // Proyectamos el centro a coordenadas normalizadas de pantalla
    const vector = center.clone().project(cam);

    const widthHalf = renderer.domElement.clientWidth / 2;
    const heightHalf = renderer.domElement.clientHeight / 2;

    // Convertimos a coordenadas en pixels dentro del canvas
    this.tooltipX = vector.x * widthHalf + widthHalf;
    this.tooltipY = -vector.y * heightHalf + heightHalf;

    this.tooltipVisible = false;
  }

  onAnimate() {
    // const mesh = this.containerMesh?.getMesh();
    // if (!mesh) return;

    // const geometry = mesh.geometry as THREE.BufferGeometry;
    // if (!this.basePositions) {
    //   this.basePositions = geometry.attributes.position.array.slice() as Float32Array;
    // }

    // const position = geometry.attributes.position as THREE.BufferAttribute;
    // const time = performance.now() * 0.001;

    // for (let i = 0; i < position.count; i++) {
    //   const x = this.basePositions[i * 3];
    //   const y = this.basePositions[i * 3 + 1];
    //   const z = this.basePositions[i * 3 + 2];

    //   if (y > (this.height * this.percentageFull) / 2 - 0.5) {
    //     let wave = 0;
    //     this.waveParams.forEach(p => {
    //       wave += Math.sin(x * p.frequency + time * p.speed) * p.height;
    //       wave += Math.cos(z * p.frequency + time * p.speed * 0.8) * p.height;
    //     });
    //     position.setY(i, y + wave);
    //   }
    // }

    // position.needsUpdate = true;
    // geometry.computeVertexNormals();
  }

  private startAnimation() {
    const renderer = this.rendererComp.getRenderer();

    const animate = () => {
      requestAnimationFrame(animate);
      const time = performance.now() * 0.001;

      // 🔹 Rotación del contenedor (puedes ajustar la velocidad)
      // const container = this.containerMesh?.getMesh();
      // if (container) {
      //   container.rotation.y += 0.01; // gira alrededor del eje Y
      // }

      // 🔹 Rotación de líquidos (opcional, si quieres que giren juntos)
      this.liquids.forEach(liquid => {
        if (liquid.mesh) {
          liquid.mesh.rotation.y += 0.01;
        }
      });

      let accumulatedHeight = -this.height / 2;

      this.liquids.forEach((liquid, index) => {
        if (!liquid.mesh || !liquid.basePositions) return;

        const geometry = liquid.mesh.geometry as THREE.BufferGeometry;
        const position = geometry.attributes.position as THREE.BufferAttribute;

        const liquidHeight = this.height * liquid.percentage;
        const topY = accumulatedHeight + liquidHeight / 2;
        const bottomY = accumulatedHeight - liquidHeight / 2;

        for (let i = 0; i < position.count; i++) {
          const x = liquid.basePositions[i * 3];
          const y = liquid.basePositions[i * 3 + 1];
          const z = liquid.basePositions[i * 3 + 2];

          let applyWave = true;   // Deveria ser false, pero para que jale todo

          if (index === 0) {
            // Primer líquido: olas solo en superficie superior
            if (y > topY - 0.25) applyWave = true;
          } else {
            // Líquidos superiores: olas arriba y abajo
            if (y > topY - 0.25 || y < bottomY + 0.5) applyWave = true;
          }

          if (applyWave) {
            let wave = 0;
            liquid.waveParams?.forEach(p => {
              wave += Math.sin(x * p.frequency + time * p.speed) * p.height;
              wave += Math.cos(z * p.frequency + time * p.speed * 0.8) * p.height;
            });
            position.setY(i, y + wave);
          }
        }

        position.needsUpdate = true;
        geometry.computeVertexNormals();
        accumulatedHeight += liquidHeight;
      });

      renderer.render(this.scene.getScene(), this.camera.getCamera());
    };

    animate();
  }

  private getMesh(index: number): THREE.Mesh | null {
    const meshRefs = this.liquidMeshes?.toArray();
    if (!meshRefs || !meshRefs[index]) return null;
    return meshRefs[index].getMesh();
  }

  getLiquidPosition(index: number) {
    let y = -(this.height / 2);
    for (let i = 0; i < index; i++) {
      y += this.liquids[i].percentage * this.height;
    }
    y += (this.liquids[index].percentage * this.height) / 2 + 0.001 * index;
    return y;
  }

  get getWidthSegments() {
    return Math.ceil(this.width / 1.5);
  }

  get getDepthSegments() {
    return Math.ceil(this.depth / 1.5);
  }
}
